/**
 * Fiche d'une démarche.
 *
 * Elle répond à trois questions sur la même page :
 * - que dois-je faire, et comment ?
 * - pourquoi cette démarche me concerne-t-elle (et d'où vient l'information) ?
 * - qu'ai-je déjà fait (historique, justificatifs) ?
 */

import { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { ApiError, saveBlob } from "../api/client";
import { documentsApi, tasksApi } from "../api/endpoints";
import { Modal } from "../components/Modal";
import { useToast } from "../components/ToastContext";
import {
  Alert,
  Button,
  Card,
  DeadlineBadge,
  DefinitionList,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  ObligationBadge,
  PageHeader,
  PriorityBadge,
  SourceBox,
  StatusBadge,
  TextAreaField,
  TextField,
  VerificationBadge,
} from "../components/ui";
import {
  useDebounced,
  useDocumentTitle,
  useMutation,
  useResource,
} from "../hooks/useApi";
import {
  formatAmount,
  formatAwaitedDistance,
  formatDate,
  formatDateTime,
  formatDeadlineDistance,
  formatFileSize,
} from "../lib/format";
import type {
  ExpectedEventOutcome,
  TaskDetail,
  TaskListItem,
  TaskStatus,
} from "../api/types";

const STATUS_FLOW: { status: TaskStatus; label: string; help: string }[] = [
  { status: "not_done", label: "Non fait", help: "Remettre la démarche à faire." },
  { status: "in_progress", label: "En cours", help: "Vous avez commencé." },
  {
    status: "waiting",
    label: "En attente",
    help: "Vous attendez un tiers (organisme, client, document).",
  },
  { status: "validated", label: "Validé", help: "La démarche est réalisée." },
];

export function TaskDetailPage() {
  const { taskId = "" } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const task = useResource(() => tasksApi.get(taskId), [taskId]);
  const history = useResource(() => tasksApi.history(taskId), [taskId]);

  const [statusTarget, setStatusTarget] = useState<TaskStatus | null>(null);
  const [eventNote, setEventNote] = useState("");
  const [eventFollowUp, setEventFollowUp] = useState<{
    message: string;
    nextStep?: string | null;
    tasks: TaskListItem[];
  } | null>(null);
  const [dependencyOpen, setDependencyOpen] = useState(false);
  const [dependencySearch, setDependencySearch] = useState("");
  const debouncedDependencySearch = useDebounced(dependencySearch, 300);
  const [note, setNote] = useState("");
  const [reference, setReference] = useState("");
  const [waitingReason, setWaitingReason] = useState("");
  const [notes, setNotes] = useState<string | null>(null);

  useDocumentTitle(task.data?.title ?? "Démarche");

  const changeStatus = useMutation(
    (status: TaskStatus) =>
      tasksApi.setStatus(taskId, {
        status,
        note: note.trim() || undefined,
        reference: reference.trim() || undefined,
        waiting_reason: waitingReason.trim() || undefined,
      }),
    {
      onSuccess: async () => {
        setStatusTarget(null);
        setNote("");
        setReference("");
        setWaitingReason("");
        await task.reload();
        await history.reload();
        toast.success("Statut mis à jour");
      },
      onError: (error) => toast.error("Changement refusé", error.message),
    },
  );

  const answerEvent = useMutation(
    (outcome: ExpectedEventOutcome) =>
      tasksApi.answerExpectedEvent(taskId, {
        outcome,
        note: eventNote.trim() || undefined,
      }),
    {
      onSuccess: async (result) => {
        setEventNote("");
        setEventFollowUp({
          message: result.message,
          nextStep: result.next_step,
          tasks: result.follow_up_tasks,
        });
        await task.reload();
        await history.reload();
        toast.success(result.outcome_label, result.message);
      },
      onError: (error) => toast.error("Réponse refusée", error.message),
    },
  );

  const saveNotes = useMutation(
    () => tasksApi.update(taskId, { user_notes: notes ?? "" }),
    {
      onSuccess: async () => {
        setNotes(null);
        await task.reload();
        await history.reload();
        toast.success("Notes enregistrées");
      },
      onError: (error) => toast.error("Enregistrement refusé", error.message),
    },
  );

  const upload = useMutation(
    (file: File) => documentsApi.upload(file, { task_id: taskId }),
    {
      onSuccess: async () => {
        await task.reload();
        await history.reload();
        toast.success("Justificatif ajouté");
      },
      onError: (error) => toast.error("Envoi refusé", error.message),
    },
  );

  const trash = useMutation(() => tasksApi.trash(taskId), {
    onSuccess: () => {
      toast.success("Démarche placée dans la corbeille");
      navigate("/demarches");
    },
    onError: (error) => toast.error("Suppression refusée", error.message),
  });

  const archive = useMutation(() => tasksApi.archive(taskId), {
    onSuccess: async () => {
      await task.reload();
      toast.success("Démarche archivée");
    },
    onError: (error) => toast.error("Archivage refusé", error.message),
  });

  const restore = useMutation(() => tasksApi.restore(taskId), {
    onSuccess: async () => {
      await task.reload();
      toast.success("Démarche restaurée");
    },
  });

  // Candidats pour une dépendance : démarches ouvertes de la même entreprise.
  const candidates = useResource(
    () =>
      tasksApi.list({
        search: debouncedDependencySearch.trim() || undefined,
        status: ["not_done", "in_progress", "waiting"],
        limit: 20,
        sort: "due",
        direction: "asc",
      }),
    [debouncedDependencySearch],
    { enabled: dependencyOpen },
  );

  const addDependency = useMutation(
    (otherId: string) => tasksApi.addDependency(taskId, otherId),
    {
      onSuccess: async () => {
        setDependencyOpen(false);
        setDependencySearch("");
        await task.reload();
        toast.success("Dépendance ajoutée");
      },
      onError: (error) => toast.error("Dépendance refusée", error.message),
    },
  );

  const removeDependency = useMutation(
    (otherId: string) => tasksApi.removeDependency(taskId, otherId),
    {
      onSuccess: async () => {
        await task.reload();
        toast.success("Dépendance retirée");
      },
    },
  );

  if (task.loading) return <LoadingBlock />;
  if (task.error) {
    return (
      <>
        <ErrorAlert error={task.error} title="Démarche introuvable" />
        <Link to="/demarches" className="btn btn--ghost mt-4">
          Retour à la liste
        </Link>
      </>
    );
  }
  const t = task.data;
  if (!t) return null;

  const downloadDocument = async (id: string) => {
    try {
      saveBlob(await documentsApi.download(id));
    } catch (error) {
      toast.error(
        "Téléchargement impossible",
        error instanceof ApiError ? error.message : undefined,
      );
    }
  };

  return (
    <>
      <PageHeader
        title={t.title}
        subtitle={
          <span className="row" style={{ gap: "0.5rem" }}>
            <StatusBadge status={t.status} label={t.status_label} />
            <PriorityBadge
              level={t.priority_level}
              label={t.priority_level_label}
              score={t.priority_score}
            />
            <ObligationBadge obligation={t.obligation} label={t.obligation_label} />
            {t.source === "manual" ? (
              <span className="badge">Démarche personnelle</span>
            ) : (
              <span className="badge">Bibliothèque administrative</span>
            )}
            <VerificationBadge
              status={t.verification_status}
              label={t.verification_status_label}
            />
          </span>
        }
        actions={
          <>
            <Button variant="ghost" onClick={() => navigate("/demarches")}>
              Retour
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate(`/demarches/${taskId}/modifier`)}
            >
              Modifier
            </Button>
          </>
        }
      />

      <div className="stack">
        {t.is_trashed && (
          <Alert
            tone="warning"
            title="Cette démarche est dans la corbeille"
            actions={
              <Button size="sm" variant="ghost" onClick={() => void restore.run()}>
                Restaurer
              </Button>
            }
          >
            Elle reste conservée et n'apparaît plus dans votre liste courante.
          </Alert>
        )}
        {t.is_archived && !t.is_trashed && (
          <Alert tone="info" title="Démarche archivée">
            Elle est conservée hors de votre liste active et reste consultable.
          </Alert>
        )}

        {t.missing_information.length > 0 && (
          <Alert tone="warning" title="Informations à compléter">
            <ul className="mb-0">
              {t.missing_information.map((item, index) => (
                <li key={`${item.kind}-${index}`}>{item.message}</li>
              ))}
            </ul>
          </Alert>
        )}

        {t.verification_status === "needs_verification" && t.verification_note && (
          <Alert tone="warning" title="Donnée à vérifier">
            {t.verification_note}
          </Alert>
        )}

        <div className="grid grid--sidebar">
          <div className="stack">
            {/* --- Événement attendu -------------------------------------- */}
            {t.kind === "expected_event" && t.expected_event && (
              <ExpectedEventCard
                task={t}
                note={eventNote}
                onNoteChange={setEventNote}
                pending={answerEvent.pending}
                onAnswer={(outcome) => void answerEvent.run(outcome)}
                followUp={eventFollowUp}
              />
            )}

            {/* --- Statut ------------------------------------------------- */}
            <Card
              title={
                t.kind === "expected_event"
                  ? "Statut de cette attente"
                  : "Où en êtes-vous ?"
              }
              hint={
                t.kind === "expected_event"
                  ? "Le statut suit votre réponse ci-dessus. Vous pouvez aussi le fixer à la main."
                  : "Chaque changement est enregistré dans l'historique."
              }
            >
              {/* Le libelle du bouton reste son nom accessible ; l'explication
                  est rattachee par aria-describedby plutot que par `title`,
                  qui masquerait le libelle pour les lecteurs d'ecran. */}
              <div className="btn-group">
                {STATUS_FLOW.map((entry) => (
                  <Button
                    key={entry.status}
                    variant={t.status === entry.status ? "primary" : "ghost"}
                    onClick={() => {
                      if (t.status === entry.status) return;
                      setStatusTarget(entry.status);
                    }}
                    aria-describedby={`status-help-${entry.status}`}
                    aria-current={t.status === entry.status ? "true" : undefined}
                  >
                    {entry.label}
                    {t.status === entry.status && (
                      <span className="visually-hidden"> (statut actuel)</span>
                    )}
                  </Button>
                ))}
              </div>
              <ul className="inline-note mt-3" style={{ paddingLeft: "1.1rem" }}>
                {STATUS_FLOW.map((entry) => (
                  <li key={entry.status} id={`status-help-${entry.status}`}>
                    <strong>{entry.label}</strong> — {entry.help}
                  </li>
                ))}
              </ul>
              {t.status === "waiting" && t.waiting_reason && (
                <p className="mt-4 mb-0">
                  <strong>En attente de :</strong> {t.waiting_reason}
                </p>
              )}
              {t.status === "validated" && (
                <div className="mt-4">
                  <p className="mb-0">
                    <strong>Validée le {formatDateTime(t.validated_at)}</strong>
                  </p>
                  {t.validation_reference && (
                    <p className="mb-0 inline-note">
                      Référence : {t.validation_reference}
                    </p>
                  )}
                  {t.validation_note && <p className="mt-3 mb-0">{t.validation_note}</p>}
                </div>
              )}
            </Card>

            {/* --- Pourquoi cette démarche ? ------------------------------ */}
            <Card title="Pourquoi cette démarche me concerne-t-elle ?">
              <p>{t.applicability_explanation ?? "Information non disponible."}</p>
              {t.applicability_factors.length > 0 && (
                <ul className="mb-0">
                  {t.applicability_factors
                    .filter((factor) => factor.label)
                    .map((factor, index) => (
                      <li key={`${factor.fact}-${index}`}>
                        {factor.missing
                          ? `${factor.label} — information manquante`
                          : factor.label}
                      </li>
                    ))}
                </ul>
              )}
            </Card>

            {/* --- Priorité ---------------------------------------------- */}
            <Card
              title="Pourquoi cette priorité ?"
              hint={t.priority_explanation ?? undefined}
            >
              {t.priority_factors.length === 0 ? (
                <p className="text-muted mb-0">Aucun facteur enregistré.</p>
              ) : (
                <div className="table-wrap">
                  <table className="data">
                    <caption className="visually-hidden">
                      Décomposition du score de priorité
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Facteur</th>
                        <th scope="col" className="numeric">
                          Points
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.priority_factors.map((factor) => (
                        <tr key={factor.code}>
                          <td>{factor.label}</td>
                          <td className="numeric">
                            {factor.points > 0 ? `+${factor.points}` : factor.points}
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td>
                          <strong>Score total</strong>
                        </td>
                        <td className="numeric">
                          <strong>{t.priority_score}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </Card>

            {/* --- Comment faire ----------------------------------------- */}
            {(t.description || t.procedure || t.required_documents.length > 0) && (
              <Card title="Comment la réaliser">
                {t.description && <p>{t.description}</p>}
                {t.procedure && (
                  <>
                    <h3>Procédure</h3>
                    <p>{t.procedure}</p>
                  </>
                )}
                {t.required_documents.length > 0 && (
                  <>
                    <h3>Documents nécessaires</h3>
                    <ul className="mb-0">
                      {t.required_documents.map((doc) => (
                        <li key={doc}>{doc}</li>
                      ))}
                    </ul>
                  </>
                )}
                {t.consequence_of_delay && (
                  <>
                    <h3>Conséquence d'un retard</h3>
                    <p className="mb-0">{t.consequence_of_delay}</p>
                  </>
                )}
              </Card>
            )}

            {/* --- Notes personnelles ------------------------------------- */}
            <Card title="Mes notes">
              {notes === null ? (
                <>
                  <p className={t.user_notes ? "" : "text-muted"}>
                    {t.user_notes || "Aucune note pour le moment."}
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setNotes(t.user_notes ?? "")}
                  >
                    {t.user_notes ? "Modifier mes notes" : "Ajouter une note"}
                  </Button>
                </>
              ) : (
                <form
                  onSubmit={(event) => {
                    event.preventDefault();
                    void saveNotes.run();
                  }}
                >
                  <TextAreaField
                    label="Notes"
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    hint="Visibles de vous seul. Conservées lors des mises à jour de la bibliothèque."
                  />
                  <div className="btn-group">
                    <Button type="submit" variant="primary" loading={saveNotes.pending}>
                      Enregistrer
                    </Button>
                    <Button variant="ghost" type="button" onClick={() => setNotes(null)}>
                      Annuler
                    </Button>
                  </div>
                </form>
              )}
            </Card>

            {/* --- Justificatifs ----------------------------------------- */}
            <Card
              title="Justificatifs"
              hint="Attestation, reçu, courrier, preuve de paiement…"
              actions={
                <>
                  <input
                    ref={fileInput}
                    type="file"
                    className="visually-hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void upload.run(file);
                      event.target.value = "";
                    }}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={upload.pending}
                    onClick={() => fileInput.current?.click()}
                  >
                    Ajouter un fichier
                  </Button>
                </>
              }
            >
              {t.documents.length === 0 ? (
                <p className="text-muted mb-0">
                  Aucun justificatif rattaché. Formats acceptés : PDF, images,
                  documents bureautiques, texte, CSV, ZIP (25 Mo maximum).
                </p>
              ) : (
                <ul className="stack stack--tight" style={{ listStyle: "none", padding: 0, margin: 0 }}>
                  {t.documents.map((document) => (
                    <li key={document.id} className="row row--between">
                      <span>
                        {document.display_name}
                        <span className="inline-note">
                          {" "}
                          · {formatFileSize(document.size_bytes)} ·{" "}
                          {formatDate(document.created_at)}
                        </span>
                      </span>
                      <Button
                        size="sm"
                        variant="quiet"
                        onClick={() => void downloadDocument(document.id)}
                      >
                        Télécharger
                      </Button>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {/* --- Dépendances ------------------------------------------- */}
            <Card
              title="Démarches liées"
              hint="Une démarche bloquée par une autre ne remonte pas en tête de vos priorités."
              actions={
                !t.is_trashed && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDependencyOpen(true)}
                  >
                    Ajouter une dépendance
                  </Button>
                )
              }
            >
              {t.dependencies.length === 0 && t.dependents.length === 0 ? (
                <p className="text-muted mb-0">
                  Aucune démarche liée. Indiquez ici ce qui doit être fait avant
                  celle-ci, par exemple un relevé à recevoir ou un document à
                  obtenir.
                </p>
              ) : (
                <>
                  {t.dependencies.length > 0 && (
                    <>
                      <h3>Cette démarche dépend de</h3>
                      <ul>
                        {t.dependencies.map((dep) => (
                          <li key={dep.id}>
                            <Link to={`/demarches/${dep.id}`}>{dep.title}</Link>{" "}
                            <StatusBadge
                              status={dep.status}
                              label={dep.status_label}
                            />{" "}
                            <button
                              type="button"
                              className="btn btn--quiet btn--sm"
                              onClick={() => void removeDependency.run(dep.id)}
                            >
                              Retirer
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {t.dependents.length > 0 && (
                    <>
                      <h3>Démarches qui en dépendent</h3>
                      <ul className="mb-0">
                        {t.dependents.map((dep) => (
                          <li key={dep.id}>
                            <Link to={`/demarches/${dep.id}`}>{dep.title}</Link>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </>
              )}
            </Card>

            {/* --- Historique -------------------------------------------- */}
            <Card title="Historique" hint="Rien n'est effacé.">
              {history.loading ? (
                <LoadingBlock />
              ) : !history.data || history.data.length === 0 ? (
                <EmptyState title="Aucun événement enregistré" />
              ) : (
                <ol className="timeline">
                  {history.data.map((entry) => (
                    <li className="timeline__item" key={entry.id}>
                      <div className="timeline__when">
                        {formatDateTime(entry.created_at)}
                      </div>
                      <div className="timeline__what">{entry.event_label}</div>
                      {entry.field_name && (
                        <div className="timeline__detail">
                          {entry.field_name}
                          {entry.old_value ? ` : « ${entry.old_value} »` : ""}
                          {entry.new_value ? ` → « ${entry.new_value} »` : ""}
                        </div>
                      )}
                      {entry.note && <div className="timeline__detail">{entry.note}</div>}
                    </li>
                  ))}
                </ol>
              )}
            </Card>
          </div>

          {/* --- Colonne latérale ----------------------------------------- */}
          <div className="stack">
            <Card title="Dates">
              <DefinitionList
                items={[
                  {
                    // Un événement attendu ne porte pas de date limite mais la
                    // fin d'un délai annoncé par un organisme : le libellé et
                    // la distance changent, sans badge de confiance d'échéance
                    // qui n'aurait pas de sens ici.
                    term:
                      t.kind === "expected_event"
                        ? "Fin du délai indicatif"
                        : "Date limite",
                    value: (
                      <>
                        {formatDate(t.due_on)}
                        <br />
                        <span className="inline-note">
                          {t.kind === "expected_event"
                            ? formatAwaitedDistance(t.days_until_due)
                            : formatDeadlineDistance(t.days_until_due)}
                        </span>
                        {t.kind !== "expected_event" && (
                          <>
                            <br />
                            <DeadlineBadge
                              confidence={t.deadline_confidence}
                              label={t.deadline_confidence_label}
                            />
                          </>
                        )}
                      </>
                    ),
                  },
                  { term: "Date conseillée", value: formatDate(t.recommended_on) },
                  {
                    term: "Réalisable à partir du",
                    value: formatDate(t.can_start_on),
                  },
                  {
                    term: "Périodicité",
                    value: t.period_label ?? t.recurrence,
                  },
                ]}
              />
              {t.deadline_note && (
                <Alert tone="info" title="À propos de cette échéance">
                  {t.deadline_note}
                </Alert>
              )}
            </Card>

            <Card title="Classement">
              <DefinitionList
                items={[
                  { term: "Catégorie", value: t.category_label },
                  { term: "Organisme", value: t.authority ?? "—" },
                  {
                    term: "Période",
                    value: t.period_label ?? t.period_key ?? "—",
                  },
                ]}
              />
            </Card>

            {t.amount_kind !== "none" && (
              <Card title="Paiement à anticiper">
                <DefinitionList
                  items={[
                    {
                      term: "Montant",
                      value:
                        t.amount_kind === "known"
                          ? formatAmount(t.amount_cents, t.amount_currency)
                          : t.amount_kind === "estimated"
                            ? `${formatAmount(t.amount_cents, t.amount_currency)} (estimé)`
                            : "Inconnu",
                    },
                    {
                      term: "Date prévisionnelle",
                      value: formatDate(t.payment_due_on ?? t.due_on),
                    },
                  ]}
                />
                {t.amount_note && <p className="inline-note mb-0">{t.amount_note}</p>}
              </Card>
            )}

            <Card title="Source">
              <SourceBox
                name={t.source_name}
                url={t.source_url}
                checkedOn={
                  t.source_checked_on ? formatDate(t.source_checked_on) : null
                }
                note={t.verification_note}
              />
              {t.rule_key && (
                <p className="inline-note mt-3 mb-0">
                  Règle : <span className="mono">{t.rule_key}</span>
                  {t.rule_version ? ` (version ${t.rule_version})` : ""}
                </p>
              )}
            </Card>

            {t.user_overrides.length > 0 && (
              <Card title="Vos modifications">
                <p className="text-muted">
                  Ces champs ont été modifiés par vous. Ils sont conservés lors
                  des mises à jour de la bibliothèque administrative :
                </p>
                <ul className="mb-0">
                  {t.user_overrides.map((field) => (
                    <li key={field} className="mono">
                      {field}
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <Card title="Autres actions">
              <div className="stack stack--tight">
                {!t.is_archived && !t.is_trashed && (
                  <Button
                    variant="ghost"
                    block
                    loading={archive.pending}
                    onClick={() => void archive.run()}
                  >
                    Archiver
                  </Button>
                )}
                {!t.is_trashed && (
                  <Button
                    variant="danger"
                    block
                    loading={trash.pending}
                    onClick={() => void trash.run()}
                  >
                    Mettre à la corbeille
                  </Button>
                )}
                {trash.error && (
                  <Alert tone="warning" title="Action impossible">
                    {trash.error.message}
                  </Alert>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* --- Modale de changement de statut ------------------------------- */}
      <Modal
        open={statusTarget !== null}
        title={
          statusTarget === "validated"
            ? "Valider cette démarche"
            : `Passer la démarche en « ${
                STATUS_FLOW.find((s) => s.status === statusTarget)?.label ?? ""
              } »`
        }
        description={
          statusTarget === "validated" ? (
            <p>
              La validation est enregistrée avec la date, votre identité et vos
              éventuelles notes. Elle restera dans votre historique.
            </p>
          ) : undefined
        }
        onClose={() => setStatusTarget(null)}
        actions={
          <Button
            variant="primary"
            loading={changeStatus.pending}
            disabled={
              statusTarget === "waiting" && !waitingReason.trim() && !note.trim()
            }
            onClick={() => statusTarget && void changeStatus.run(statusTarget)}
          >
            Confirmer
          </Button>
        }
      >
        {statusTarget === "waiting" && (
          <TextField
            label="Qu'attendez-vous ?"
            value={waitingReason}
            onChange={(event) => setWaitingReason(event.target.value)}
            hint="Exemple : réponse de l'Urssaf, relevé bancaire, retour du client."
            required
          />
        )}
        {statusTarget === "validated" && (
          <TextField
            label="Référence"
            optional
            value={reference}
            onChange={(event) => setReference(event.target.value)}
            hint="Numéro de déclaration, accusé de réception, référence de paiement…"
          />
        )}
        <TextAreaField
          label="Note"
          optional
          value={note}
          onChange={(event) => setNote(event.target.value)}
          hint="Elle sera conservée dans l'historique."
        />
        {changeStatus.error && (
          <Alert tone="critical" title="Changement refusé">
            {changeStatus.error.message}
          </Alert>
        )}
      </Modal>

      {/* --- Modale d'ajout de dépendance --------------------------------- */}
      <Modal
        open={dependencyOpen}
        title="Cette démarche dépend de…"
        description={
          <p>
            Choisissez la démarche qui doit être terminée avant celle-ci. Tant
            qu'elle ne l'est pas, cette démarche restera signalée comme bloquée
            et ne remontera pas en tête de vos priorités.
          </p>
        }
        onClose={() => {
          setDependencyOpen(false);
          setDependencySearch("");
        }}
      >
        <TextField
          label="Rechercher une démarche"
          type="search"
          value={dependencySearch}
          onChange={(event) => setDependencySearch(event.target.value)}
          placeholder="Titre, organisme…"
        />
        {addDependency.error && (
          <Alert tone="critical" title="Dépendance refusée">
            {addDependency.error.message}
          </Alert>
        )}
        {candidates.loading ? (
          <LoadingBlock />
        ) : (
          <ul
            className="stack stack--tight"
            style={{ listStyle: "none", padding: 0, margin: 0 }}
          >
            {(candidates.data?.items ?? [])
              .filter(
                (candidate) =>
                  candidate.id !== taskId &&
                  !t.dependencies.some((dep) => dep.id === candidate.id),
              )
              .map((candidate) => (
                <li key={candidate.id} className="row row--between">
                  <span>
                    {candidate.title}
                    <span className="inline-note">
                      {" "}
                      · {candidate.status_label}
                      {candidate.due_on
                        ? ` · échéance ${formatDate(candidate.due_on)}`
                        : ""}
                    </span>
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={addDependency.pending}
                    onClick={() => void addDependency.run(candidate.id)}
                  >
                    Choisir
                  </Button>
                </li>
              ))}
            {(candidates.data?.items ?? []).length === 0 && (
              <li className="text-muted">Aucune démarche ouverte ne correspond.</li>
            )}
          </ul>
        )}
      </Modal>
    </>
  );
}


/**
 * Panneau d'un événement attendu.
 *
 * L'application ne peut pas savoir si un organisme a envoyé quelque chose :
 * elle pose la question et enregistre la réponse. Le texte affiché est celui
 * copié dans l'occurrence, pas celui de la règle courante — une attente déjà
 * tranchée garde la question telle qu'elle était posée à l'époque.
 */
function ExpectedEventCard({
  task,
  note,
  onNoteChange,
  pending,
  onAnswer,
  followUp,
}: {
  task: TaskDetail;
  note: string;
  onNoteChange: (value: string) => void;
  pending: boolean;
  onAnswer: (outcome: ExpectedEventOutcome) => void;
  followUp: {
    message: string;
    nextStep?: string | null;
    tasks: TaskListItem[];
  } | null;
}) {
  const event = task.expected_event;
  if (!event) return null;
  const answered = task.event_outcome && task.event_outcome !== "awaiting";

  return (
    <Card
      title={event.question}
      hint={`Élément attendu de : ${event.organisation}. Vous n'avez rien à faire, vous avez quelque chose à recevoir puis à confirmer ici.`}
    >
      {task.indicative_delay_elapsed && (
        <Alert tone="warning" title="Le délai indicatif est écoulé">
          {event.if_not_received}
        </Alert>
      )}

      {event.delay_sentence && (
        <p className="mb-0">
          <strong>Délai :</strong> {event.delay_sentence}
        </p>
      )}
      {event.delay_is_official && event.delay_statement && (
        <p className="source-box__quote">« {event.delay_statement} »</p>
      )}
      {event.confirmation_mode && (
        <p className="inline-note">
          <strong>Où le trouver :</strong> {event.confirmation_mode}
        </p>
      )}

      {answered ? (
        <div className="mt-4">
          <p className="mb-0">
            <strong>Votre réponse :</strong> {task.event_outcome_label}
            {task.event_outcome_at && ` — ${formatDateTime(task.event_outcome_at)}`}
          </p>
          {task.event_outcome_note && (
            <p className="mt-3 mb-0">{task.event_outcome_note}</p>
          )}
          <p className="inline-note mt-3 mb-0">
            Vous pouvez modifier votre réponse ci-dessous si la situation a
            changé. Chaque réponse est conservée dans l'historique.
          </p>
        </div>
      ) : null}

      <div className="mt-4">
        <TextAreaField
          label="Précision"
          optional
          rows={2}
          value={note}
          onChange={(fieldEvent) => onNoteChange(fieldEvent.target.value)}
          hint="Par exemple la référence du courrier, ou ce qui est demandé."
        />
        <div className="btn-group">
          {event.answers.map((answer) => (
            <Button
              key={answer.value}
              variant={
                task.event_outcome === answer.value
                  ? "primary"
                  : answer.value === "received"
                    ? "default"
                    : "ghost"
              }
              loading={pending}
              onClick={() => onAnswer(answer.value)}
              aria-describedby={`event-help-${answer.value}`}
              aria-current={task.event_outcome === answer.value ? "true" : undefined}
            >
              {answer.label}
              {task.event_outcome === answer.value && (
                <span className="visually-hidden"> (réponse actuelle)</span>
              )}
            </Button>
          ))}
        </div>
        {/* Les suites de chaque réponse sont annoncées avant de cliquer : on ne
            découvre pas l'effet après coup. Rattachées par aria-describedby
            pour ne pas masquer le libellé des boutons. */}
        <ul className="inline-note mt-3" style={{ paddingLeft: "1.1rem" }}>
          {event.answers
            .filter((answer) => answer.next)
            .map((answer) => (
              <li key={answer.value} id={`event-help-${answer.value}`}>
                <strong>{answer.label}</strong> — {answer.next}
              </li>
            ))}
        </ul>
      </div>

      {followUp && (
        <Alert tone="info" title="La suite">
          <p>{followUp.message}</p>
          {followUp.nextStep && <p>{followUp.nextStep}</p>}
          {followUp.tasks.length > 0 && (
            <ul className="mb-0">
              {followUp.tasks.map((item) => (
                <li key={item.id}>
                  <Link to={`/demarches/${item.id}`}>{item.title}</Link>
                </li>
              ))}
            </ul>
          )}
        </Alert>
      )}
    </Card>
  );
}
